import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const resolveUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/**
 * Lightbox
 * Props: photos [{url,caption?}], index, onClose, onIndex(n)
 */
export const Lightbox = ({ photos, index, onClose, onIndex }) => {
  const open = index !== null && index !== undefined && index >= 0;

  const next = useCallback(() => {
    if (!open) return;
    onIndex((index + 1) % photos.length);
  }, [open, index, photos.length, onIndex]);

  const prev = useCallback(() => {
    if (!open) return;
    onIndex((index - 1 + photos.length) % photos.length);
  }, [open, index, photos.length, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={onClose}
          data-testid="lightbox"
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 z-10 text-white/80 hover:text-white p-2"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={26} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 md:left-8 z-10 text-white/70 hover:text-[var(--gold)] p-3 hidden md:block"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 md:right-8 z-10 text-white/70 hover:text-[var(--gold)] p-3 hidden md:block"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight size={40} />
          </button>

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative max-w-6xl max-h-full w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveUrl(photos[index].url)}
              alt={photos[index].caption || ""}
              className="max-h-[82vh] w-auto max-w-full object-contain"
            />
            <div className="mt-4 flex items-center justify-between w-full font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/50">
              <span>{photos[index].caption || photos[index].client || ""}</span>
              <span data-testid="lightbox-counter">{index + 1} / {photos.length}</span>
            </div>
            {/* Mobile nav */}
            <div className="md:hidden mt-4 flex items-center gap-3">
              <button onClick={prev} className="btn-ghost-light !px-3 !py-2" aria-label="prev"><ChevronLeft size={16} /></button>
              <button onClick={next} className="btn-ghost-light !px-3 !py-2" aria-label="next"><ChevronRight size={16} /></button>
            </div>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { resolveUrl };
export default Lightbox;
